import {useCallback, useEffect, useState} from 'react';
import api from '../api/axiosInstance';
import {format} from 'date-fns';

export default function AdminOrdersPage() {
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [orders, setOrders] = useState([]);
    const [filters, setFilters] = useState({
        shippingStatus: '',
        customer: '',
        startDate: '',
        endDate: ''
    });
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);

    const fetchOrders = useCallback(
        async (page) => {
            try {
                const params = {
                    page,
                    ...(filters.shippingStatus && {shippingStatus: filters.shippingStatus}),
                    ...(filters.customer && {username: filters.customer}),
                    ...(filters.startDate && {start: filters.startDate}),
                    ...(filters.endDate && {end: filters.endDate}),
                };

                const res = await api.get('/admin/orders', {params});
                setOrders(res.data.orders || []);
                setTotalPages(res.data.totalPages || 1);
                setSelectedOrders([]);
                setSelectAll(false);
            } catch (err) {
                console.error('Failed to fetch orders', err);
            }
        },
        [filters]
    );

    useEffect(() => {
        fetchOrders(currentPage);
    }, [currentPage, fetchOrders]);

    const toggleSelectAll = () => {
        if (selectAll) {
            setSelectedOrders([]);
        } else {
            setSelectedOrders(orders.map(o => o._id));
        }
        setSelectAll(!selectAll);
    };

    const toggleSelectOrder = (id) => {
        setSelectedOrders((prev) =>
            prev.includes(id) ? prev.filter(o => o !== id) : [...prev, id]
        );
    };

    const exportCSV = () => {
        const selected = orders.filter(o => selectedOrders.includes(o._id));
        const rows = [];

        selected.forEach(order => {
            order.items.forEach(item => {
                rows.push({
                    name: order.username,
                    address: `"${order.shippingAddress.line1}, ${order.shippingAddress.city}, ${order.shippingAddress.zipcode}"`,
                    sku: item.sku || '',
                    title: item.title,
                    quantity: item.quantity
                });
            });
        });

        const csvContent = [
            ['name', 'address', 'sku', 'title', 'quantity'],
            ...rows.map(r => [r.name, r.address, r.sku, r.title, r.quantity])
        ]
            .map(e => e.join(','))
            .join('\n');

        const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `orders.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const updateStatus = async (id, newStatus) => {
        try {
            await api.patch(`/admin/orders/${id}/status`, {shippingStatus: newStatus});
            fetchOrders(currentPage);
        } catch (err) {
            console.error('Failed to update status', err);
        }
    };

    const resendEmail = async (id) => {
        try {
            await api.post(`/admin/orders/${id}/resend`);
            alert('Email resent.');
        } catch (err) {
            console.error('Failed to resend email', err);
        }
    };

    const cancelOrder = async (id) => {
        try {
            await updateStatus(id, 'cancelled');
        } catch (err) {
            console.error('Failed to cancel order', err);
        }
    };

    const processNewOrders = async () => {
        try {
            await api.post('/admin/orders/process');
            await fetchOrders(currentPage);
        } catch (err) {
            console.error('Failed to process orders', err);
            alert('Failed to process orders. See console for details.');
        }
    };

    const Pagination = () => (
        <div className="flex justify-center items-center gap-4 my-6">
            <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
            >
                Previous
            </button>
            <span className="text-sm">Page {currentPage} of {totalPages}</span>
            <button
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
            >
                Next
            </button>
        </div>
    );

    return (
        <div className="p-6">
            <h1 className="text-2xl font-bold mb-6">Order Management</h1>

            <div className="flex flex-wrap gap-4 mb-6">
                <input
                    type="text"
                    placeholder="Customer Username"
                    value={filters.customer}
                    onChange={e => setFilters({...filters, customer: e.target.value})}
                    className="border px-3 py-2 rounded"
                />
                <select
                    value={filters.shippingStatus}
                    onChange={e => setFilters({...filters, shippingStatus: e.target.value})}
                    className="border px-3 py-2 rounded"
                >
                    <option value="">All Statuses</option>
                    <option value="unfulfilled">Unfulfilled</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="cancelled by user">Cancelled By User</option>
                </select>
                <input
                    type="date"
                    value={filters.startDate}
                    onChange={e => setFilters({...filters, startDate: e.target.value})}
                    className="border px-3 py-2 rounded"
                />
                <input
                    type="date"
                    value={filters.endDate}
                    onChange={e => setFilters({...filters, endDate: e.target.value})}
                    className="border px-3 py-2 rounded"
                />
                <button
                    onClick={() => {
                        setCurrentPage(1);
                        fetchOrders(1);
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded"
                >
                    Apply Filters
                </button>

                <div className="ml-auto flex gap-2">
                    <button
                        onClick={exportCSV}
                        className="bg-green-600 text-white px-4 py-2 rounded"
                        disabled={selectedOrders.length === 0}
                    >
                        Export CSV
                    </button>

                    <button
                        onClick={processNewOrders}
                        className="bg-purple-600 text-white px-4 py-2 rounded"
                    >
                        Process Orders
                    </button>
                </div>
            </div>

            <Pagination/>

            <div className="overflow-x-auto border rounded">
                <table className="min-w-full table-auto text-sm">
                    <thead className="bg-gray-100">
                    <tr>
                        <th className="px-4 py-2 text-left">
                            <input
                                type="checkbox"
                                checked={selectAll}
                                onChange={toggleSelectAll}
                            />
                        </th>
                        <th className="px-4 py-2 text-left w-[18%]">Order ID</th>
                        <th className="px-4 py-2 text-left w-[14%]">Customer</th>
                        <th className="px-4 py-2 text-left w-[12%]">Date</th>
                        <th className="px-4 py-2 text-left w-[14%]">Shipping Status</th>
                        <th className="px-4 py-2 text-left w-[14%]">Payment Status</th>
                        <th className="px-4 py-2 text-left w-[10%]">Total</th>
                        <th className="px-4 py-2 text-left w-[18%]">Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {orders.map((order) => (
                        <tr key={order._id} className="border-t even:bg-gray-100">
                            <td className="px-4 py-2">
                                <input
                                    type="checkbox"
                                    checked={selectedOrders.includes(order._id)}
                                    onChange={() => toggleSelectOrder(order._id)}
                                />
                            </td>
                            <td className="px-4 py-2 truncate">{order._id}</td>
                            <td className="px-4 py-2">{order.username}</td>
                            <td className="px-4 py-2">{format(new Date(order.createdAt), 'MMM d, yyyy')}</td>
                            <td className="px-4 py-2 capitalize">{order.shippingStatus}</td>
                            <td className="px-4 py-2 capitalize">{order.paymentStatus}</td>
                            <td className="px-4 py-2">${order.total?.toFixed(2)}</td>
                            <td className="px-4 py-2 space-x-2 whitespace-nowrap">
                                <button onClick={() => setSelectedOrder(order)}
                                        className="text-blue-600 underline">View
                                </button>
                                <button onClick={() => updateStatus(order._id, 'processing')}
                                        className="text-violet-600 underline">Process
                                </button>
                                <button onClick={() => updateStatus(order._id, 'shipped')}
                                        className="text-green-600 underline">Ship
                                </button>
                                <button onClick={() => resendEmail(order._id)}
                                        className="text-yellow-600 underline">Resend
                                </button>
                                <button onClick={() => cancelOrder(order._id)}
                                        className="text-red-600 underline">Cancel
                                </button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            <Pagination/>

            {selectedOrder && (
                <div className="mt-8 p-4 border rounded bg-gray-50">
                    <h2 className="text-xl font-semibold mb-4">Order Details</h2>
                    <p><strong>Customer:</strong> {selectedOrder.username}</p>
                    <p><strong>Payment Status:</strong> {selectedOrder.paymentStatus}</p>
                    <p><strong>Shipping Status:</strong> {selectedOrder.shippingStatus}</p>
                    <p><strong>Shipping
                        Address:</strong> {selectedOrder.shippingAddress.line1}, {selectedOrder.shippingAddress?.city}, {selectedOrder.shippingAddress?.zipcode}
                    </p>
                    <p><strong>Tracking:</strong> {selectedOrder.shipping?.tracking || '—'}</p>
                    <div className="mt-4">
                        <h3 className="font-bold mb-2">Items:</h3>
                        <ul className="list-disc ml-6">
                            {selectedOrder.items.map((item, idx) => (
                                <li key={idx}>
                                    {item.title} (x{item.quantity}) - ${item.price.toFixed(2)}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <button
                        onClick={() => setSelectedOrder(null)}
                        className="mt-4 text-blue-600 underline"
                    >
                        Close
                    </button>
                </div>
            )}
        </div>
    );
}
